<template>
  <div
    class="w-full h-full px-5 pt-[14px] pb-[22px] bg-gray-200/20 rounded-xl mb-[25px]"
  >
    <div class="flex items-center gap-x-2.5 mb-2">
      <Icon :name="icon" :size="22" class="stroke-gray-800" />

      <div
        class="font-secondary font-medium text-[15px] leading-[20px] text-gray-800"
      >
        {{ title }}
      </div>
    </div>

    <div class="w-full h-px mb-4 bg-gray-200/50"></div>

    <div class="font-secondary text-[12px] leading-[16px] text-gray-500 mb-4">
      {{ matchTitle }}
    </div>

    <Wave
      :url="match.amplitudeUrl"
      :stem="stem"
      :regions="regions"
      :type="type"
      :level="match.level ?? undefined"
      :label="props.number"
      class="mb-10"
    />

    <div class="w-full pl-14 relative mb-1">
      <div
        class="w-px h-4 absolute -top-full"
        :class="{
          'bg-gray-300': type === 'original',
          ...(type === 'infringing' && {
            'bg-red-300': match.level === ScoreLevel.High,
            'bg-yellow-300': match.level === ScoreLevel.Medium,
            'bg-green-300': match.level === ScoreLevel.Low,
          }),
        }"
        :style="{
          left: `max(${markerPosition}%, 56px)`,
        }"
      ></div>

      <div
        class="h-[15px] border border-b-0 rounded-t-[3px]"
        :class="{
          'border-gray-300': type === 'original',
          ...(type === 'infringing' && {
            'border-red-300': match.level === ScoreLevel.High,
            'border-yellow-400': match.level === ScoreLevel.Medium,
            'border-green-400': match.level === ScoreLevel.Low,
          }),
        }"
      ></div>
    </div>

    <div class="mb-1.5 flex items-center gap-x-5">
      <div
        v-if="canPlayMatch"
        class="min-w-9 h-9 border border-gray-200 rounded-full flex items-center justify-center cursor-pointer"
        @click="handleClick"
      >
        <Icon
          :name="playbackIcon"
          :size="18"
          :style="{
            marginLeft: playbackIcon === 'Play' ? '3px' : null,
          }"
        />
      </div>

      <div
        class="w-full h-[50px] flex items-center justify-center border rounded-[3px]"
        :class="{
          'border-gray-500 bg-gray-500/10': type === 'original',
          ...(type === 'infringing' && {
            'border-red-500 bg-red-500/10': match.level === ScoreLevel.High,
            'border-yellow-500 bg-yellow-500/10':
              match.level === ScoreLevel.Medium,
            'border-green-500 bg-green-500/10': match.level === ScoreLevel.Low,
          }),
        }"
      >
        <div v-if="canPlayMatch" ref="audioEl" class="w-full"></div>
        <canvas v-else ref="isolatedWaveCanvas" class="w-full h-10"></canvas>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'

export const AudioMatchFragment = graphql(`
  fragment AudioMatchFields on AudioMatch {
    id
    location
    summary
    level
    matchingUrl
    amplitudeUrl
    start
    end
    duration
    markers {
      color
      shine
      visible
    }
    stem {
      name
      url
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import { ScoreLevel } from '@/shared/graphql/router/graphql'
import { Icon, type IconName } from '@dacr/ui'
import Wave, { type Region } from '@/features/copyright/components/Wave.vue'
import {
  parseWaveformData,
  type WaveformStem,
} from '@/features/copyright/utils/waveform'
import axios from 'axios'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps<{
  match: FragmentType<typeof AudioMatchFragment>
  variant: 'original' | 'stem'
  number: number
  allowPlayback?: boolean
}>()

const match = useFragment(AudioMatchFragment, props.match)

const canPlayMatch = computed(() => props.allowPlayback !== false)

const type = computed(() => (match.summary ? 'infringing' : 'original'))

const title = computed(() =>
  props.variant === 'original' ? 'Original audio' : match.stem.name
)

const stem = computed(() =>
  props.variant === 'stem' ? match.stem.name.toLowerCase() : undefined
)

const matchTitle = computed(() =>
  props.variant === 'stem' ? 'Isolated match' : 'Matching segment'
)

const url = computed(() =>
  props.variant === 'original' ? match.matchingUrl : match.stem.url
)

const icon = computed<IconName>(() => {
  if (stem.value === 'vocals') {
    return 'Mic'
  }

  if (stem.value === 'drums') {
    return 'Drums'
  }

  if (stem.value === 'guitar') {
    return 'Guitar'
  }

  if (stem.value === 'bass') {
    return 'Bass'
  }

  return 'Audio'
})

const regions = computed<Region[]>(() => [
  {
    id: match.id,
    start: Number(match.start),
    end: Number(match.end),
    label: match.location,
    type:
      match.level === ScoreLevel.High
        ? 'danger'
        : match.level === ScoreLevel.Medium
          ? 'warning'
          : 'normal',
  },
])

const markerPosition = computed(
  () =>
    ((Number(match.start) + Number(match.end)) / 2 / Number(match.duration)) *
    100
)

const wavesurfer = ref<InstanceType<typeof import('wavesurfer.js').default>>()
const audioEl = ref<HTMLElement>()
const isolatedWaveCanvas = ref<HTMLCanvasElement>()
const playbackIcon = ref<IconName>('Play')
let isolatedWaveResizeObserver: ResizeObserver | null = null
let isolatedWaveResizeTimeout: ReturnType<typeof setTimeout> | null = null

const handleClick = () => {
  if (!canPlayMatch.value) return
  wavesurfer.value?.playPause()
  playbackIcon.value = wavesurfer.value?.isPlaying() ? 'Pause' : 'Play'
}

const getWaveColor = () => {
  if (type.value === 'original') {
    return '#6E7480'
  }

  if (match.level === ScoreLevel.High) {
    return 'rgba(238, 136, 138)'
  }

  if (match.level === ScoreLevel.Medium) {
    return 'rgba(245, 185, 88)'
  }

  return 'rgba(96, 204, 214)'
}

const getProgressColor = () => {
  if (type.value === 'original') {
    return '#454b54'
  }

  if (match.level === ScoreLevel.High) {
    return '#DC3C3F'
  }

  if (match.level === ScoreLevel.Medium) {
    return '#CE773A'
  }

  return '#1B95AB'
}

const getIsolatedWaveStem = (data: {
  main: WaveformStem | null
  stems: WaveformStem[]
}) => {
  if (props.variant === 'original') return data.main
  return data.stems.find((s) => s.name === stem.value) ?? null
}

const getStemPointTime = (stemData: WaveformStem, index: number) =>
  stemData.timepoints[index] ??
  (index / Math.max(1, stemData.max.length - 1)) * stemData.duration

const drawIsolatedWaveform = (stemData: WaveformStem) => {
  const canvas = isolatedWaveCanvas.value
  if (!canvas) return

  const ctx = canvas.getContext('2d')
  const rect = canvas.getBoundingClientRect()
  if (!ctx || !rect.width || !rect.height) return

  const dpr = window.devicePixelRatio || 1
  const width = Math.max(1, Math.round(rect.width * dpr))
  const height = Math.max(1, Math.round(rect.height * dpr))
  canvas.width = width
  canvas.height = height
  ctx.clearRect(0, 0, width, height)

  const start = Number(match.start)
  const end = Number(match.end)
  const startIndex = stemData.max.findIndex(
    (_, index) => getStemPointTime(stemData, index) >= start
  )
  const first = startIndex === -1 ? 0 : startIndex
  const last = stemData.max.findLastIndex(
    (_, index) => getStemPointTime(stemData, index) <= end
  )
  const final = last === -1 ? stemData.max.length - 1 : last
  const sampleCount = final - first + 1
  if (sampleCount <= 0) return

  const centerY = Math.round(height / 2)
  const bucketsPerColumn = sampleCount / width
  ctx.fillStyle = getWaveColor()

  for (let x = 0; x < width; x++) {
    const bucketStart = first + Math.floor(x * bucketsPerColumn)
    const bucketEnd = Math.min(
      final + 1,
      Math.max(bucketStart + 1, first + Math.floor((x + 1) * bucketsPerColumn))
    )

    let max = 0
    let min = 0
    for (let index = bucketStart; index < bucketEnd; index++) {
      max = Math.max(max, stemData.max[index] ?? 0)
      min = Math.min(min, stemData.min[index] ?? 0)
    }

    const top = Math.round(max * centerY)
    const bottom = Math.round(-min * (height - centerY))
    if (top > 0) ctx.fillRect(x, centerY - top, 1, top)
    if (bottom > 0) ctx.fillRect(x, centerY, 1, bottom)
  }
}

const renderIsolatedWaveform = async () => {
  const result = await axios.get(match.amplitudeUrl)
  const stemData = getIsolatedWaveStem(parseWaveformData(result.data))
  if (!stemData) return

  await nextTick()
  drawIsolatedWaveform(stemData)

  if (!isolatedWaveCanvas.value) return
  isolatedWaveResizeObserver = new ResizeObserver(() => {
    if (isolatedWaveResizeTimeout) clearTimeout(isolatedWaveResizeTimeout)
    isolatedWaveResizeTimeout = setTimeout(() => {
      drawIsolatedWaveform(stemData)
    }, 100)
  })
  isolatedWaveResizeObserver.observe(isolatedWaveCanvas.value)
}

onMounted(async () => {
  if (!canPlayMatch.value) {
    renderIsolatedWaveform()
    return
  }
  if (!audioEl.value) return

  const WaveSurfer = (await import('wavesurfer.js')).default

  wavesurfer.value = WaveSurfer.create({
    container: audioEl.value,
    waveColor: getWaveColor(),
    progressColor: getProgressColor(),
    url: url.value,
    height: 40,
    cursorWidth: 0,
  })
})

onUnmounted(() => {
  isolatedWaveResizeObserver?.disconnect()
  isolatedWaveResizeObserver = null
  if (isolatedWaveResizeTimeout) {
    clearTimeout(isolatedWaveResizeTimeout)
    isolatedWaveResizeTimeout = null
  }
})
</script>
